/**
 * Bidirectional Adjustments Mapping
 *
 * Converts between Sati GPU pipeline preset format and Tank frontend adjustments format.
 * This mirrors the Python implementation in src/contracts/adjustments.py
 *
 * Sati format: Based on src/sati/gpu/presets.py Preset dataclass
 * Tank format: Based on src/tank/src/utils/imageAdjustments.js DEFAULT_ADJUSTMENTS
 */

import { DEFAULT_ADJUSTMENTS } from '../utils/imageAdjustments';

/**
 * Convert Sati applied_settings to Tank adjustments format.
 *
 * @param {Object} appliedSettings - The applied_settings from meta.json
 * @returns {Object} Tank adjustments object compatible with DEFAULT_ADJUSTMENTS
 */
export function satiToTank(appliedSettings) {
  if (!appliedSettings || !appliedSettings.tank_format) {
    return { ...DEFAULT_ADJUSTMENTS };
  }

  // The tank_format is already in the correct format from Python
  const tank = appliedSettings.tank_format;

  return {
    exposure: tank.exposure ?? 0,
    contrast: tank.contrast ?? 0,
    highlights: tank.highlights ?? 0,
    shadows: tank.shadows ?? 0,
    whites: tank.whites ?? 0,
    blacks: tank.blacks ?? 0,
    clarity: tank.clarity ?? 0,
    dehaze: tank.dehaze ?? 0,
    vibrance: tank.vibrance ?? 0,
    saturation: tank.saturation ?? 0,
    temperature: tank.temperature ?? 0,
    tint: tank.tint ?? 0,
    sharpening: {
      amount: tank.sharpening?.amount ?? 0,
      radius: tank.sharpening?.radius ?? 0.5,
    },
    color_grading: {
      lift: {
        r: tank.color_grading?.lift?.r ?? 0,
        g: tank.color_grading?.lift?.g ?? 0,
        b: tank.color_grading?.lift?.b ?? 0,
      },
      gamma: {
        r: tank.color_grading?.gamma?.r ?? 1,
        g: tank.color_grading?.gamma?.g ?? 1,
        b: tank.color_grading?.gamma?.b ?? 1,
      },
      gain: {
        r: tank.color_grading?.gain?.r ?? 1,
        g: tank.color_grading?.gain?.g ?? 1,
        b: tank.color_grading?.gain?.b ?? 1,
      },
    },
    tone_curve: tank.tone_curve ?? null,
    denoise: tank.denoise ?? 0,
  };
}

/**
 * Convert raw Sati preset dict to Tank adjustments (for direct preset loading).
 *
 * Use this when you have a raw preset dict (not from applied_settings).
 * Normally you should use satiToTank() with applied_settings from meta.json.
 *
 * @param {Object} presetDict - Raw Sati preset dictionary
 * @returns {Object} Tank adjustments object
 */
export function satiPresetToTank(presetDict) {
  if (!presetDict) {
    return { ...DEFAULT_ADJUSTMENTS };
  }

  const exposure = presetDict.exposure || {};
  const wb = presetDict.white_balance || {};
  const sharpen = presetDict.sharpen || {};
  const denoise = presetDict.denoise || {};
  const vibranceSection = presetDict.vibrance || {};
  const dehazeSection = presetDict.dehaze || {};
  const toneCurveSection = presetDict.tone_curve || {};

  // Contrast: now stored directly as -100 to 100
  // Backwards compat: old presets stored in tonemap.contrast (1.0-2.5 range)
  let tankContrast;
  if ('contrast' in presetDict && typeof presetDict.contrast === 'number') {
    tankContrast = Math.round(presetDict.contrast);
  } else {
    const tonemap = presetDict.tonemap || {};
    const oldContrast = tonemap.contrast ?? 1.0;
    tankContrast = Math.round((oldContrast - 1.0) * 66.67);
  }
  tankContrast = Math.max(-100, Math.min(100, tankContrast));

  // Convert white balance: Sati -1..1 to Tank -100..100
  const tankTemperature = Math.round((wb.temperature ?? 0) * 100);
  const tankTint = Math.round((wb.tint ?? 0) * 100);

  // Color grading (new format with lift/gamma/gain)
  let liftR = 0, liftG = 0, liftB = 0;
  let gammaR = 1, gammaG = 1, gammaB = 1;
  let gainR = 1, gainG = 1, gainB = 1;

  const colorGrading = presetDict.color_grading;
  if (colorGrading) {
    const lift = colorGrading.lift || [0, 0, 0];
    const gamma = colorGrading.gamma || [1, 1, 1];
    const gain = colorGrading.gain || [1, 1, 1];
    liftR = Array.isArray(lift) ? lift[0] : 0;
    liftG = Array.isArray(lift) ? lift[1] : 0;
    liftB = Array.isArray(lift) ? lift[2] : 0;
    gammaR = Array.isArray(gamma) ? gamma[0] : 1;
    gammaG = Array.isArray(gamma) ? gamma[1] : 1;
    gammaB = Array.isArray(gamma) ? gamma[2] : 1;
    gainR = Array.isArray(gain) ? gain[0] : 1;
    gainG = Array.isArray(gain) ? gain[1] : 1;
    gainB = Array.isArray(gain) ? gain[2] : 1;
  } else {
    // Backwards compat: old color_balance format with multipliers
    const colorBalance = presetDict.color_balance || {};
    const shadows = colorBalance.shadows || [1, 1, 1];
    const midtones = colorBalance.midtones || [1, 1, 1];
    const highlights = colorBalance.highlights || [1, 1, 1];
    liftR = Array.isArray(shadows) ? shadows[0] - 1.0 : 0;
    liftG = Array.isArray(shadows) ? shadows[1] - 1.0 : 0;
    liftB = Array.isArray(shadows) ? shadows[2] - 1.0 : 0;
    gammaR = Array.isArray(midtones) ? midtones[0] : 1;
    gammaG = Array.isArray(midtones) ? midtones[1] : 1;
    gammaB = Array.isArray(midtones) ? midtones[2] : 1;
    gainR = Array.isArray(highlights) ? highlights[0] : 1;
    gainG = Array.isArray(highlights) ? highlights[1] : 1;
    gainB = Array.isArray(highlights) ? highlights[2] : 1;
  }

  // Tone sliders: now stored directly as -100 to 100
  // Backwards compat: old presets used -1 to 1 range
  const toneSliders = presetDict.tone_sliders || {};
  let rawHighlights = toneSliders.highlights ?? 0;
  let rawShadows = toneSliders.shadows ?? 0;
  let rawWhites = toneSliders.whites ?? 0;
  let rawBlacks = toneSliders.blacks ?? 0;

  const tsValues = [rawShadows, rawHighlights, rawWhites, rawBlacks].filter(v => Math.abs(v) > 1e-6);
  if (tsValues.length > 0 && tsValues.every(v => Math.abs(v) <= 1.0)) {
    rawShadows *= 100;
    rawHighlights *= 100;
    rawWhites *= 100;
    rawBlacks *= 100;
  }

  // Clarity: read directly
  const claritySection = presetDict.clarity;
  const tankClarity = claritySection != null
    ? Math.round(typeof claritySection === 'object' ? (claritySection.amount ?? 0) : claritySection)
    : 0;

  // Convert vibrance/saturation: Sati -1..1 to Tank -100..100
  const tankVibrance = Math.round((vibranceSection.vibrance ?? 0) * 100);
  const tankSaturation = Math.round((vibranceSection.saturation ?? 0) * 100);

  // Convert dehaze: Sati 0..1 to Tank 0..100
  const tankDehaze = Math.round((dehazeSection.amount ?? 0) * 100);

  // Convert tone curve: Sati 0..1 points to Tank 0..255 points
  const satiTcPoints = toneCurveSection.points || [];
  const tankToneCurve = satiTcPoints.length > 0
    ? satiTcPoints.map(([x, y]) => [Math.round(x * 255), Math.round(y * 255)])
    : null;

  // Convert denoise: Sati 0..1 to Tank 0..100
  const denoiseEnabled = denoise.enabled ?? false;
  const denoiseStrength = denoise.strength ?? 0;
  const tankDenoise = denoiseEnabled ? Math.round(denoiseStrength * 100) : 0;

  return {
    exposure: exposure.ev ?? 0,
    contrast: tankContrast,
    highlights: Math.round(rawHighlights),
    shadows: Math.round(rawShadows),
    whites: Math.round(rawWhites),
    blacks: Math.round(rawBlacks),
    clarity: tankClarity,
    dehaze: tankDehaze,
    vibrance: tankVibrance,
    saturation: tankSaturation,
    temperature: tankTemperature,
    tint: tankTint,
    sharpening: {
      amount: sharpen.amount ?? 0,
      radius: sharpen.radius ?? 0.5,
    },
    color_grading: {
      lift: { r: liftR, g: liftG, b: liftB },
      gamma: { r: gammaR, g: gammaG, b: gammaB },
      gain: { r: gainR, g: gainG, b: gainB },
    },
    tone_curve: tankToneCurve,
    denoise: tankDenoise,
  };
}

/**
 * Convert Tank adjustments to Sati preset format for re-export.
 *
 * @param {Object} tankAdjustments - Tank adjustments object
 * @param {Object} preserveNative - Optional sati_native to preserve lossless values
 * @returns {Object} Sati preset dictionary
 */
export function tankToSati(tankAdjustments, preserveNative = null) {
  const tank = tankAdjustments || {};
  const native = preserveNative || {};
  const nativeSharpen = native.sharpen || {};
  const nativeDenoise = native.denoise || {};

  // Convert white balance: Tank -100..100 to Sati -1..1
  const satiTemperature = (tank.temperature || 0) / 100;
  const satiTint = (tank.tint || 0) / 100;

  // Color grading: pass lift/gamma/gain directly
  const cg = tank.color_grading || {};
  const lift = cg.lift || { r: 0, g: 0, b: 0 };
  const gamma = cg.gamma || { r: 1, g: 1, b: 1 };
  const gain = cg.gain || { r: 1, g: 1, b: 1 };

  // Convert vibrance/saturation: Tank -100..100 to Sati -1..1
  const satiVibrance = (tank.vibrance || 0) / 100;
  const satiSaturation = (tank.saturation || 0) / 100;

  // Convert dehaze: Tank 0..100 to Sati 0..1
  const satiDehaze = (tank.dehaze || 0) / 100;

  // Convert tone curve: Tank 0..255 points to Sati 0..1 points
  const tankTc = tank.tone_curve;
  const satiTcPoints = Array.isArray(tankTc) && tankTc.length > 0
    ? tankTc.map(([x, y]) => [x / 255, y / 255])
    : [];

  // Convert denoise: Tank 0..100 to Sati 0..1
  const satiDenoiseStrength = (tank.denoise || 0) / 100;
  const satiDenoiseEnabled = (tank.denoise || 0) > 0;

  return {
    version: 1,
    name: 'Tank Export',
    description: 'Settings exported from Tank editor',
    exposure: { ev: tank.exposure || 0 },
    contrast: tank.contrast || 0,
    tone_sliders: {
      shadows: tank.shadows || 0,
      highlights: tank.highlights || 0,
      whites: tank.whites || 0,
      blacks: tank.blacks || 0,
    },
    clarity: {
      amount: tank.clarity || 0,
    },
    white_balance: {
      temperature: satiTemperature,
      tint: satiTint,
    },
    tone_curve: {
      points: satiTcPoints,
    },
    color_grading: {
      lift: [lift.r, lift.g, lift.b],
      gamma: [gamma.r, gamma.g, gamma.b],
      gain: [gain.r, gain.g, gain.b],
    },
    vibrance: {
      vibrance: satiVibrance,
      saturation: satiSaturation,
    },
    dehaze: {
      amount: satiDehaze,
    },
    sharpen: {
      amount: tank.sharpening?.amount ?? 0,
      radius: tank.sharpening?.radius ?? 0.5,
      threshold: nativeSharpen.threshold ?? 0,
    },
    denoise: {
      enabled: satiDenoiseEnabled,
      model: nativeDenoise.model || 'nafnet',
      strength: satiDenoiseStrength,
    },
  };
}

/**
 * Extract sati_native from applied_settings for roundtrip preservation.
 *
 * @param {Object} appliedSettings - The applied_settings from meta.json
 * @returns {Object|null} The sati_native section or null
 */
export function extractSatiNative(appliedSettings) {
  if (!appliedSettings || !appliedSettings.sati_native) {
    return null;
  }
  return appliedSettings.sati_native;
}

/**
 * Check if adjustments differ from the Sati-applied settings.
 *
 * @param {Object} currentAdjustments - Current Tank adjustments
 * @param {Object} appliedSettings - Original applied_settings from meta.json
 * @returns {boolean} True if user has modified settings
 */
export function hasUserModifications(currentAdjustments, appliedSettings) {
  if (!appliedSettings || !appliedSettings.tank_format) {
    // No original settings, any non-default is a modification
    return true;
  }

  const original = appliedSettings.tank_format;
  const current = currentAdjustments;

  // Compare basic numeric values
  const basicKeys = [
    'exposure',
    'contrast',
    'highlights',
    'shadows',
    'whites',
    'blacks',
    'clarity',
    'dehaze',
    'vibrance',
    'saturation',
    'temperature',
    'tint',
    'denoise',
  ];

  for (const key of basicKeys) {
    if ((current[key] ?? 0) !== (original[key] ?? 0)) {
      return true;
    }
  }

  // Compare sharpening
  if (
    (current.sharpening?.amount ?? 0) !== (original.sharpening?.amount ?? 0) ||
    (current.sharpening?.radius ?? 0.5) !== (original.sharpening?.radius ?? 0.5)
  ) {
    return true;
  }

  // Compare tone curve
  const currentTc = current.tone_curve ?? null;
  const originalTc = original.tone_curve ?? null;
  if (currentTc === null && originalTc !== null) return true;
  if (currentTc !== null && originalTc === null) return true;
  if (currentTc !== null && originalTc !== null) {
    if (currentTc.length !== originalTc.length) return true;
    for (let i = 0; i < currentTc.length; i++) {
      if (currentTc[i][0] !== originalTc[i][0] || currentTc[i][1] !== originalTc[i][1]) {
        return true;
      }
    }
  }

  // Compare color grading (simplified check)
  const cgCurrent = current.color_grading || {};
  const cgOriginal = original.color_grading || {};

  for (const zone of ['lift', 'gamma', 'gain']) {
    for (const channel of ['r', 'g', 'b']) {
      const defaultVal = zone === 'lift' ? 0 : 1;
      if (
        (cgCurrent[zone]?.[channel] ?? defaultVal) !==
        (cgOriginal[zone]?.[channel] ?? defaultVal)
      ) {
        return true;
      }
    }
  }

  return false;
}
